import React, { useState } from 'react';
import {
  Dropdown,
  Badge,
  ListGroup,
  Button,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use notification context for notifications with pagination
  const {
    notifications,
    unreadCount,
    hasMore,
    isLoading,
    isConnected,
    toggleRead,
    archiveNotification,
    deleteNotification,
    loadMore,
  } = useNotifications();

  const showError = (message: string) => {
    setError(message);
    // Auto-dismiss after 5 seconds
    setTimeout(() => setError(null), 5000);
  };

  const handleToggleRead = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await toggleRead(notificationId);
    } catch (error: any) {
      console.error('Failed to toggle read state:', error);
      showError(`Failed to toggle read state: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleArchive = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await archiveNotification(notificationId);
    } catch (error: any) {
      console.error('Failed to archive notification:', error);
      showError(`Failed to archive: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
    } catch (error: any) {
      console.error('Failed to delete notification:', error);
      showError(`Failed to delete: ${error?.message || 'Unknown error'}`);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'commission':
        return 'bi-currency-dollar';
      case 'payment':
        return 'bi-credit-card';
      case 'quota':
        return 'bi-target';
      case 'deal':
        return 'bi-briefcase';
      case 'system':
        return 'bi-gear';
      case 'timesheet':
        return 'bi-clock';
      default:
        return 'bi-bell';
    }
  };

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'light';
    }
  };

  return (
    <>
      <Dropdown show={isOpen} onToggle={setIsOpen}>
        <Dropdown.Toggle
          variant="link"
          id="notifications-dropdown"
          className="border-0 position-relative text-body text-decoration-none p-2"
          style={{ backgroundColor: 'transparent' }}
        >
          <i className={`bi bi-bell${isConnected ? '-fill' : ''} fs-5`}></i>
          {unreadCount > 0 && (
            <Badge
              bg="danger"
              className="position-absolute top-0 start-100 translate-middle badge-sm rounded-pill"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu
          align="end"
          style={{ width: '350px', maxHeight: '400px', overflowY: 'auto' }}
          className="shadow"
        >
          <div className="px-3 py-2 border-bottom">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Notifications</h6>
              {unreadCount > 0 && (
                <Badge bg="primary" className="rounded-pill">
                  {unreadCount} new
                </Badge>
              )}
            </div>
          </div>

          {notifications.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-bell-slash fs-3 mb-2 d-block"></i>
              No notifications
            </div>
          ) : (
            <>
              <ListGroup variant="flush" className="border-0">
                {notifications.map((notification) => (
                  <ListGroup.Item
                    key={notification.id}
                    className={`border-0 border-bottom ${notification.read ? 'bg-body-tertiary' : ''}`}
                  >
                    <div className="d-flex align-items-start">
                      <div className="me-3 mt-1">
                        <i
                          className={`${getNotificationIcon(notification.type || '')} fs-5 text-${getPriorityVariant(notification.priority || '')}`}
                        ></i>
                      </div>
                      <div className="flex-grow-1 min-width-0">
                        <div className="d-flex justify-content-between align-items-start">
                          <h6 className="mb-1 text-truncate" style={{ fontSize: '0.875rem' }}>
                            {notification.title}
                          </h6>
                          <small className="text-muted ms-2 text-nowrap">
                            {notification.created_at ? format(new Date(notification.created_at), 'MMM d') : ''}
                          </small>
                        </div>
                        <p className="mb-1 text-muted small">
                          {notification.message}
                        </p>
                        {notification.data?.action_url ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 mb-1 small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsOpen(false);
                              navigate(notification.data!.action_url as string);
                            }}
                          >
                            {(notification.data.action_label as string) || 'View'}
                          </Button>
                        ) : null}
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <Button
                            variant={notification.read ? "outline-secondary" : "outline-primary"}
                            size="sm"
                            className="py-0 px-1"
                            title={notification.read ? "Mark as unread" : "Mark as read"}
                            onClick={(e) => notification.id && handleToggleRead(e, notification.id)}
                          >
                            <i className={`bi ${notification.read ? 'bi-eye-slash' : 'bi-eye'}`} style={{ fontSize: '0.75rem' }}></i>
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="py-0 px-1"
                            title="Archive"
                            onClick={(e) => notification.id && handleArchive(e, notification.id)}
                          >
                            <i className="bi bi-archive" style={{ fontSize: '0.75rem' }}></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="py-0 px-1"
                            title="Delete"
                            onClick={(e) => notification.id && handleDelete(e, notification.id)}
                          >
                            <i className="bi bi-x-lg" style={{ fontSize: '0.75rem' }}></i>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              {hasMore && (
                <div className="text-center py-2 border-top">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => loadMore()}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Loading...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>

      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={!!error}
          onClose={() => setError(null)}
          bg="danger"
          autohide
          delay={5000}
        >
          <Toast.Header>
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong className="me-auto">Error</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{error}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default NotificationDropdown;
